import { useState, useEffect, useRef } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { projectDoc } from '../constants/firestorePaths';
import { docChangedMessage, isRefusedAsChanged } from '../utils/versionedDoc';
import { functions } from '../firebase';
import { CLOUD_FUNCTIONS } from '../constants/firebase';
import { createProjectErrorMessage } from '../features/projects/projectLimitMessage';
import { useToast } from '../contexts/ToastContext';
import StudySetupForm from './StudySetupForm';
import { StudySetup, DEFAULT_STUDY_SETUP } from '../constants/studySetupOptions';
import { ChevronRight, Save, Loader2 } from 'lucide-react';

/** Plain names for the fields the server may refuse (reason `bad_study_setup`). */
const FIELD_NAMES: Record<string, string> = {
  countries: 'Countries',
  blinding: 'Blinding',
  randomized: 'Randomized',
  productType: 'What the study tests',
  oversight: 'Oversight body',
  parties: 'Parties & Responsibilities',
  safetyReporting: 'Safety Reporting',
  labModel: 'Laboratories',
  imagingModel: 'Imaging',
  externalDataSourceExclusions: 'External Data Sources',
  ctmsInterface: 'CTMS',
  protocolDeviationEcrf: 'Protocol deviations',
  dictionaryVersions: 'Coding Dictionaries',
  timelines: 'Timelines',
  studyParts: 'Study parts',
  edcVersion: 'EDC version',
  hostingProvider: 'Hosting provider',
  hostingRegion: 'Hosting region',
  subjectIdFormat: 'Subject ID format',
  crfDateFormat: 'Date format on case report forms',
  cdiscStudyId: 'CDISC STUDYID',
};

/**
 * Drop rows the user added and left blank (a party with no name, a study part
 * with no label) before saving. They carry no information, and the server
 * refuses them.
 */
function withoutBlankRows(setup: Partial<StudySetup>): Partial<StudySetup> {
  const next = { ...setup };
  if (next.parties) {
    const parties = next.parties.filter((p) => p.name && p.name.trim());
    if (parties.length) next.parties = parties;
    else delete next.parties;
  }
  if (next.studyParts) {
    const parts = next.studyParts.filter((p) => p.id && p.id.trim());
    if (parts.length) next.studyParts = parts;
    else delete next.studyParts;
  }
  return next;
}

interface StudySetupPanelProps {
  projectId: string;
  onClose: () => void;
}

export default function StudySetupPanel({ projectId, onClose }: StudySetupPanelProps) {
  const [setup, setSetup] = useState<Partial<StudySetup>>(DEFAULT_STUDY_SETUP);
  const [projectName, setProjectName] = useState('');
  const [shortName, setShortName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const { showToast, dismissToast } = useToast();
  /**
   * What Firestore last told us this study holds.
   *
   * A REFUSED save writes nothing, so no snapshot arrives and the inputs keep
   * the value that was just rejected — press Save again and it fails again,
   * identically. That is what produced the same duplicate refusal twice in the
   * console on 2026-08-26. This is what a refusal reverts TO.
   */
  const storedRef = useRef<{ name: string; shortName: string }>({ name: '', shortName: '' });
  /**
   * The error toast this panel last raised, so a later SUCCESS can clear it.
   * Error toasts are persistent by design (ToastContext) — right for a failure
   * carrying a reference code, wrong when the problem has since been fixed and
   * the message is still sitting there saying otherwise.
   */
  const errorToastRef = useRef<number | null>(null);
  /**
   * Optimistic concurrency: the studySettingsVersion of the copy this panel
   * shows (the listener below always takes on the stored values), sent with
   * each save as its base. Once a save is refused, the panel stops saving.
   */
  const settingsVersionRef = useRef(0);
  const settingsStaleRef = useRef(false);

  useEffect(() => {
    const unsub = onSnapshot(projectDoc(projectId), (snap) => {
      const data = snap.data();
      if (data) {
        setSetup(data.studySetup || DEFAULT_STUDY_SETUP);
        setProjectName(data.name || '');
        setShortName(data.shortName || '');
        // The snapshot is the only source of what is actually stored, and a
        // refused save produces none — so remember it here or a refusal has
        // nothing to restore.
        storedRef.current = { name: data.name || '', shortName: data.shortName || '' };
        const storedVersion = data.studySettingsVersion;
        if (typeof storedVersion === 'number' && storedVersion > settingsVersionRef.current) {
          settingsVersionRef.current = storedVersion;
        }
      }
    });
    return unsub;
  }, [projectId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (settingsStaleRef.current) {
        showToast(docChangedMessage('The study settings'), 'error');
        return;
      }
      // Strip undefined values — Firestore rejects them
      const clean = withoutBlankRows(JSON.parse(JSON.stringify(setup)));
      const trimmedName = projectName.trim();
      const trimmedShort = shortName.trim();
      if (!trimmedName) {
        showToast('Project name is required', 'error');
        setSaving(false);
        return;
      }
      // The Short Name is required and is never substituted. Clearing the field
      // used to silently store the first five characters of the project name,
      // uppercased — so a user who deleted the value got a machine-made one on
      // every filename without being told.
      if (!trimmedShort) {
        showToast(
          'Study Short Name is required — it appears on every document filename.',
          'error',
        );
        setSaving(false);
        return;
      }
      // Saved through the SERVER, not written straight to Firestore.
      //
      // Until 2026-08-26 this was `updateDoc(projectDoc(...), ...)`, so the
      // Short Name rules that guard project CREATION did not apply to an edit:
      // a study could be renamed into a duplicate of another, and two studies
      // that share a Short Name produce documents with the same filename. The
      // check above stays so the user gets an immediate message, but it is
      // advisory — anything in a browser can be bypassed by whoever has the
      // page. `saveStudySettingsV2` is the enforcement.
      const saveFn = httpsCallable(functions, CLOUD_FUNCTIONS.SAVE_STUDY_SETTINGS);
      const res = await saveFn({
        projectId,
        studySetup: clean,
        name: trimmedName,
        shortName: trimmedShort,
        // The version this panel's copy came from; the save is refused if
        // the stored settings have moved since.
        baseSettingsVersion: settingsVersionRef.current,
      });
      const written = (res.data as { settingsVersion?: unknown } | null)?.settingsVersion;
      if (typeof written === 'number' && written > settingsVersionRef.current) {
        settingsVersionRef.current = written;
      }
      // The save landed, so any error this panel is still showing is about a
      // problem that no longer exists. Clear it — persistent errors are right
      // until they are superseded.
      if (errorToastRef.current !== null) {
        dismissToast(errorToastRef.current);
        errorToastRef.current = null;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      // The server's refusals carry a machine-readable `reason`; this maps it to
      // the sentence for that reason rather than showing raw server prose.
      console.error('Failed to save study setup:', err);
      const details = (err as { details?: { reason?: unknown; field?: unknown } } | null)?.details;
      if (details?.reason === 'bad_study_setup') {
        // Nothing was written. The server names the field; say which one in
        // plain words and leave the user's input in place to correct.
        const field = typeof details.field === 'string' ? details.field : '';
        errorToastRef.current = showToast(
          `One of the study settings could not be saved: ${FIELD_NAMES[field] ?? 'a setting'} is not in the expected form. Nothing was changed.`,
          'error',
        );
        return;
      }
      if (isRefusedAsChanged(err, 'settings-changed')) {
        // Nothing was written, and this panel's copy is older than what is
        // stored: say so plainly and stop saving until the page is reloaded.
        settingsStaleRef.current = true;
        errorToastRef.current = showToast(docChangedMessage('The study settings'), 'error');
        return;
      }
      // Put the inputs back to what is actually stored. Nothing was written, so
      // leaving the rejected value in the box would only let the same save fail
      // the same way — and it would misrepresent the study as holding a value
      // it does not hold.
      setProjectName(storedRef.current.name);
      setShortName(storedRef.current.shortName);
      errorToastRef.current = showToast(createProjectErrorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside className="w-96 bg-white border-l border-gray-200/60 flex flex-col absolute right-0 top-14 bottom-0 shadow-elevated animate-slide-in-right">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <h2 className="font-semibold text-gray-900 text-sm">Study Settings</h2>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronRight className="h-4 w-4 text-gray-400" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Project identity — top-level project fields, not inside studySetup */}
        <div className="space-y-3 pb-4 border-b border-gray-100">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Project Name</label>
            <input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              placeholder="e.g. Phase II Cardiology Study"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Short Name</label>
            <input
              value={shortName}
              onChange={(e) => setShortName(e.target.value.toUpperCase())}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm uppercase focus:ring-2 focus:ring-primary-500"
              placeholder="e.g. CARD-2"
            />
            <p className="text-[11px] text-gray-400 mt-1">Used as a compact identifier across the app.</p>
          </div>
        </div>

        <StudySetupForm
          value={setup}
          onChange={setSetup}
          compact={true}
        />
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-100">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : saved ? (
            'Saved'
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
